import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { SocketService } from './services/socket.service';
import { SecretService } from './services/secret.service';
import { UserAdminstrationService } from './services/user-adminstration.service';
import { AuthGuard } from './guard/auth.guard';
import { ThemeService } from './services/theme.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  title = 'ttc-pmt-app';

  constructor(
    private socketService: SocketService,
    private secretService: SecretService,
    private userAdminService: UserAdminstrationService,
    private authGuard: AuthGuard,
    private router: Router,
    private themeService: ThemeService
  ) {}

  ngOnInit(): void {
    const userStr = localStorage.getItem('user');
    const token = localStorage.getItem('token');

    // Subscribed unconditionally, and before the token check below. This runs
    // once at bootstrap — which for a fresh visitor is BEFORE they log in — so
    // gating it on a token would mean the listener was never attached for the
    // session they actually go on to use.
    this.listenForAclChanges();

    if (userStr && token) {
      try {
        const user = JSON.parse(userStr);
        if (user && user._id) {
          console.log('[AppComponent] Reconnecting socket for user:', user._id);
          this.socketService.connect(user._id);
        }
      } catch (e) {
        console.error('[AppComponent] Error parsing user from localStorage', e);
      }

      // Menu access is cached in localStorage at login, so a change made in the
      // PMT Controller used to stay invisible until the user logged out and back
      // in. Two things fix that: this refresh on every boot, which covers anyone
      // who was offline when the change happened, and the live push registered
      // above.
      this.refreshMenuAccess();
    }
  }

  /** The role this browser is signed in as, or '' if it cannot be read. */
  private currentRoleId(): string {
    try {
      return (
        this.secretService.decryptLocalStorageData(
          localStorage.getItem('role_id')
        ) || ''
      );
    } catch {
      return '';
    }
  }

  /**
   * Re-read this role's menu access from the server. `aclPermission` already
   * writes localStorage and pushes to `dataService.menu`, which the sidebar
   * subscribes to — so the menu redraws with no reload.
   */
  private refreshMenuAccess(): void {
    const roleId = this.currentRoleId();
    if (!roleId) return;

    this.userAdminService.aclPermission({ role_id: roleId }).subscribe({
      next: () => this.leaveRouteIfNoLongerAllowed(),
      // A failed refresh must not disturb the session — the cached menu simply
      // stays in place until the next attempt.
      error: (err) =>
        console.warn('[AppComponent] Could not refresh menu access:', err),
    });
  }

  /**
   * Reload the menu whenever the Controller changes this role's access.
   *
   * Subscribes to the service's subject rather than the socket directly: the
   * socket object is thrown away and rebuilt on every logout/login, so a
   * listener bound to it would stop firing after the next sign-in. The subject
   * outlives that, so subscribing once here is enough.
   */
  private listenForAclChanges(): void {
    this.socketService.aclUpdated$.subscribe(() => {
      console.log('[AppComponent] Role access changed — reloading menu.');
      this.refreshMenuAccess();
    });
  }

  /**
   * If the page the user is sitting on was just revoked, move them off it —
   * otherwise they keep seeing a screen they no longer have rights to until
   * they happen to navigate.
   *
   * The check is delegated to AuthGuard.checkAuthorizedRoutes rather than
   * re-implemented here. That matters: the guard strips the url slug and also
   * consults commonRoutes and sharedRoutes, so a private copy of the matching
   * would evict people from pages they are still entitled to.
   */
  private leaveRouteIfNoLongerAllowed(): void {
    const url = this.router.url;

    // Same precondition the guard itself applies — only slug'd admin routes are
    // permission-checked; anything else it lets through untouched.
    if (url.search('admin') === -1) return;

    if (!this.authGuard.checkAuthorizedRoutes(url, 'admin')) {
      console.log('[AppComponent] Current page no longer permitted.');
      this.router.navigate(['/admin/dashboard']);
    }
  }
}
